import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import RangeModal from "./RangeModal";
import ScaleSettings from "./ScaleSettings";

// Колонка параметров печати (.set-col экрана 5 редизайна):
// выпадающий список «Страницы» + три сегмент-переключателя + счётчик копий.
const MAX_COPIES = 15;

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.08 },
    }),
};

const CARD =
    "bg-white rounded-[2.6vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] px-[1.8vw] py-[1.4vh] flex flex-col justify-center gap-[1vh] flex-1 min-h-0";
const LABEL =
    "text-center text-[1.15vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]";

// disabledOptions — точечно недоступные варианты (режимы аппарата: цвет,
// дуплекс). disabled — переключатель выключен целиком: выбранный вариант
// остаётся видимым, но приглушённым, чтобы было понятно, что печать всё же
// в каком-то режиме идёт, просто менять его нельзя.
function Segment({ analyticsId, value, options, onChange, t, disabledOptions = [], disabled = false }) {
    return (
        <div
            className={`flex bg-[#F1F3F7] rounded-full p-[0.4vh] ${
                disabled ? "opacity-60" : ""
            }`}
        >
            {options.map((option) => {
                const isDisabled = disabled || disabledOptions.includes(option);
                const isActive = value === option;
                return (
                    <button data-analytics={`${analyticsId}.${option}`}
                        key={option}
                        type="button"
                        onClick={() => !isDisabled && onChange(option)}
                        disabled={isDisabled}
                        className={`flex-1 min-w-0 px-[0.5vw] py-[1vh] rounded-full text-[1.25vh] font-bold leading-snug transition-colors ${
                            disabled
                                ? isActive
                                    ? "bg-[#C9CFD9] text-white cursor-not-allowed"
                                    : "text-[#B8BEC9] cursor-not-allowed"
                                : isDisabled
                                  ? "text-[#B8BEC9] opacity-50 cursor-not-allowed"
                                  : isActive
                                    ? "bg-[#246CE2] text-white"
                                    : "text-[#7B8290]"
                        }`}
                    >
                        {t(option)}
                    </button>
                );
            })}
        </div>
    );
}

function PrintSettings({
    printType,
    setPrintType,
    customPages,
    setCustomPages,
    orientation,
    setOrientation,
    printSide,
    setPrintSide,
    printColor,
    setPrintColor,
    copyCount,
    setCopyCount,
    totalPages,
    // Режимы работы аппарата (GET /api/v1/payments/price, удалённое управление
    // из веб-панели/бота) — при false соответствующая опция недоступна для выбора,
    // а не просто "показана, но платёж потом упадёт с ошибкой".
    allowColor = true,
    allowDuplex = true,
    allowOrientation = false,
    allowScaling = false,
    scaleMode = "auto",
    scalePercent = 100,
    setScaleMode,
    setScalePercent,
}) {
    const { t } = useTranslation();
    const [pagesOpen, setPagesOpen] = useState(false);
    const [rangeOpen, setRangeOpen] = useState(false);

    const pages = totalPages || 1;

    // Подпись справа от пункта списка: сколько страниц уйдёт в печать при этом режиме.
    const hintFor = (type) => {
        if (type === "print_all") return t("editDoc.pages_count", { count: pages });
        if (type === "custom") {
            return customPages || t("editDoc.pages_choose");
        }
        return t("editDoc.pages_count", { count: 1 });
    };

    const handleSelect = (type) => {
        setPagesOpen(false);
        // «Диапазон…» сначала спрашивает страницы и только потом меняет режим —
        // иначе отмена оставила бы printType=custom с пустым списком страниц.
        if (type === "custom") {
            setRangeOpen(true);
            return;
        }
        setPrintType(type);
        if (type === "first_page") {
            setCustomPages("1");
        } else if (type === "last_page") {
            setCustomPages(pages.toString());
        } else {
            setCustomPages("");
        }
    };

    const changeCopies = (delta) => {
        const next = Math.min(MAX_COPIES, Math.max(1, (copyCount || 1) + delta));
        setCopyCount?.(next);
    };

    return (
        <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-[1.2vh]">
            {/* Страницы */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={0}
                className={`${CARD} relative z-20`}
            >
                <span className={LABEL}>{t("editDoc.pages_label")}</span>
                <div className="relative">
                    <button data-analytics="printsettings.setPagesOpen.2"
                        type="button"
                        onClick={() => setPagesOpen((open) => !open)}
                        aria-expanded={pagesOpen}
                        className={`w-full rounded-full px-[1.4vw] py-[1.2vh] flex items-center justify-between gap-[0.8vw] text-[1.5vh] font-bold transition-colors ${
                            pagesOpen
                                ? "bg-[#E3ECFB] text-[#246CE2]"
                                : "bg-[#F1F3F7] text-[#12161F]"
                        }`}
                    >
                        {/* Длинное название переносится, число страниц остаётся целиком. */}
                        <span className="flex items-baseline gap-[0.5vw] min-w-0">
                            <span className="min-w-0 text-left leading-snug">
                                {t(`editDoc.print_types.${printType}`)}
                            </span>
                            <span className="flex-none text-[1.25vh] font-semibold opacity-70">
                                {hintFor(printType)}
                            </span>
                        </span>
                        <svg
                            className={`w-[1.8vh] h-[1.8vh] flex-none transition-transform ${
                                pagesOpen ? "rotate-180" : ""
                            }`}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>

                    <AnimatePresence>
                        {pagesOpen && (
                            <>
                                {/* клик мимо списка — закрыть */}
                                <div
                                    className="fixed inset-0 z-10"
                                    onClick={() => setPagesOpen(false)}
                                />
                                <motion.div
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.18 }}
                                    className="absolute top-[calc(100%+0.8vh)] left-0 right-0 z-20 bg-white rounded-[2.2vh] shadow-[0_2vh_6vh_rgba(18,22,31,0.18)] p-[0.6vh] flex flex-col gap-[0.2vh]"
                                >
                                    {[
                                        "print_all",
                                        "first_page",
                                        "last_page",
                                        "custom",
                                    ].map((type) => {
                                        const active = printType === type;
                                        return (
                                            <button data-analytics="printsettings.handleSelect.3"
                                                key={type}
                                                type="button"
                                                onClick={() =>
                                                    handleSelect(type)
                                                }
                                                className={`rounded-[1.6vh] px-[1.4vw] py-[1.2vh] flex items-center justify-between gap-[1vw] text-[1.5vh] font-bold text-left transition-colors ${
                                                    active
                                                        ? "bg-[#246CE2] text-white"
                                                        : "text-[#12161F] active:bg-[#F1F3F7]"
                                                }`}
                                            >
                                                <span className="min-w-0 leading-snug">
                                                    {t(
                                                        `editDoc.print_types.${type}`,
                                                    )}
                                                </span>
                                                <span
                                                    className={`text-[1.25vh] font-semibold flex-none ${
                                                        active
                                                            ? "text-white/75"
                                                            : "text-[#7B8290]"
                                                    }`}
                                                >
                                                    {hintFor(type)}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            {/* Цвет */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={1}
                className={CARD}
            >
                <span className={LABEL}>{t("editDoc.color_label")}</span>
                <Segment
                    analyticsId="settings.color"
                    value={printColor}
                    options={["black_white", "color"]}
                    onChange={setPrintColor}
                    t={(key) => t(`editDoc.print_colors.${key}`)}
                    disabledOptions={allowColor ? [] : ["color"]}
                />
            </motion.div>

            {/* Стороны листа */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className={CARD}
            >
                <span className={LABEL}>{t("editDoc.sides_label")}</span>
                <Segment
                    analyticsId="settings.sides"
                    value={printSide}
                    options={["one_sided", "two_sided"]}
                    onChange={setPrintSide}
                    t={(key) => t(`editDoc.print_sides.${key}`)}
                    disabledOptions={allowDuplex ? [] : ["two_sided"]}
                />
            </motion.div>

            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={3}
                className={CARD}
            >
                <span className={LABEL}>{t("editDoc.orientation_label")}</span>
                <Segment
                    analyticsId="settings.orientation"
                    value={orientation}
                    options={["portrait", "landscape"]}
                    onChange={setOrientation}
                    t={(key) => t(`editDoc.orientations.${key}`)}
                    disabled={!allowOrientation}
                />
            </motion.div>

            {/* Копии. Заголовок сверху, как у остальных карточек: в строку с
                счётчиком он не помещался и обрезался («КӨШІРМ…» на казахском). */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={4}
                className={CARD}
            >
                <span className={LABEL}>{t("editDoc.copies_label")}</span>
                <div className="flex items-center justify-center gap-[1.6vw]">
                    <button data-analytics="printsettings.changeCopies.4"
                        type="button"
                        onClick={() => changeCopies(-1)}
                        disabled={(copyCount || 1) <= 1}
                        className="flex-none w-[5vh] h-[5vh] rounded-full bg-[#F1F3F7] text-[2.6vh] font-bold leading-none disabled:opacity-30 active:bg-[#246CE2] active:text-white transition-colors"
                    >
                        −
                    </button>
                    <span className="text-[3.2vh] font-extrabold min-w-[4vh] text-center">
                        {copyCount || 1}
                    </span>
                    <button data-analytics="printsettings.changeCopies.5"
                        type="button"
                        onClick={() => changeCopies(1)}
                        disabled={(copyCount || 1) >= MAX_COPIES}
                        className="flex-none w-[5vh] h-[5vh] rounded-full bg-[#F1F3F7] text-[2.6vh] font-bold leading-none disabled:opacity-30 active:bg-[#246CE2] active:text-white transition-colors"
                    >
                        +
                    </button>
                </div>
            </motion.div>

            {allowScaling && <ScaleSettings mode={scaleMode} percent={scalePercent} onModeChange={setScaleMode} onPercentChange={setScalePercent}
                className={CARD} />}

            <AnimatePresence>
                {rangeOpen && (
                    <RangeModal
                        value={printType === "custom" ? customPages : ""}
                        totalPages={pages}
                        onApply={(value) => {
                            setCustomPages(value);
                            setPrintType("custom");
                            setRangeOpen(false);
                        }}
                        onCancel={() => setRangeOpen(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

export default PrintSettings;
